import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  FileEdit, 
  Calendar, 
  Settings,
  Menu,
  X,
  LogOut
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';

const ShowcaseDashboardLayout = ({ children, language, activeTab, setActiveTab }) => {
  const { t, lang } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // navItems mit id, die zum activeTab matched
  const navItems = [
    { id: 'kreation', label: lang === 'en' ? 'Creation' : 'Kreation', icon: Sparkles, description: lang === 'en' ? 'Generate content ideas' : 'Content-Ideen generieren' },
    { id: 'produktion', label: lang === 'en' ? 'Production' : 'Produktion', icon: FileEdit, description: lang === 'en' ? 'Edit content' : 'Content bearbeiten' },
    { id: 'distribution', label: 'Distribution', icon: Calendar, description: lang === 'en' ? 'Plan content' : 'Content planen' },
    { id: 'einstellungen', label: lang === 'en' ? 'Settings' : 'Einstellungen', icon: Settings, description: lang === 'en' ? 'Profile & Company' : 'Profil & Unternehmen', disabled: true },
  ];

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleNavClick = (item) => {
    if (item.disabled) {
      toast.info(lang === 'en' ? 'Settings not available in Showcase mode' : 'Einstellungen nicht in Showcase verfügbar');
      return;
    }
    if (setActiveTab) setActiveTab(item.id);
    closeMobileMenu();
  };

  return (
    <div className="min-h-screen bg-[#0A1628] flex flex-col lg:flex-row">
      {/* Mobile Header */}
      <header className="lg:hidden flex items-center justify-between p-4 border-b border-[#1A2B42]">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_kreation-hub/artifacts/haikxkku_xchannelsai_full_name_logo_transparent_colour.png" 
            alt="xchannels.ai" 
            className="h-8 shrink-0"
          />
        </div>
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-white"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={closeMobileMenu}
          />
        )}
      </AnimatePresence>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: 'tween', duration: 0.2 }}
            className="lg:hidden fixed left-0 top-0 bottom-0 w-[280px] bg-[#0A1628] border-r border-[#1A2B42] z-50 flex flex-col"
          >
            <div className="p-4 border-b border-[#1A2B42]">
              <img 
                src="https://customer-assets.emergentagent.com/job_kreation-hub/artifacts/haikxkku_xchannelsai_full_name_logo_transparent_colour.png" 
                alt="xchannels.ai" 
                className="h-8 shrink-0"
              />
            </div>

            <nav className="flex-1 p-4 overflow-y-auto">
              <ul className="space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = !item.disabled && activeTab === item.id;
                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => handleNavClick(item)}
                        data-testid={`mobile-nav-${item.id}`}
                        className={`
                          w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left
                          ${isActive 
                            ? 'bg-[#1A2B42] text-[#00D9C8] border-l-2 border-[#00D9C8]' 
                            : item.disabled
                              ? 'text-[#5A6C7D] hover:bg-[#1A2B42]/30 cursor-not-allowed'
                              : 'text-[#A0AEC0] hover:text-white hover:bg-[#1A2B42]/50'
                          }
                        `}
                      >
                        <Icon className="w-5 h-5" />
                        <div>
                          <p className="font-medium">{item.label}</p>
                          <p className="text-xs opacity-70">{item.description}</p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="p-4 border-t border-[#1A2B42] space-y-4">
              <div className="flex items-center gap-3 px-3">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-[#00D9C8]/20 text-[#00D9C8] text-sm">
                    XC
                  </AvatarFallback>
                </Avatar>
                <div className="text-left">
                  <p className="text-sm font-medium text-white">Demo User</p>
                  <p className="text-xs text-[#5A6C7D]">demo@xchannels.ai</p>
                </div>
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start text-red-400 hover:bg-[#1A2B42] hover:text-red-400"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 border-r border-[#1A2B42] flex-col bg-[#0A1628]">
        <div className="p-6 border-b border-[#1A2B42]">
          <img 
            src="https://customer-assets.emergentagent.com/job_kreation-hub/artifacts/haikxkku_xchannelsai_full_name_logo_transparent_colour.png" 
            alt="xchannels.ai" 
            className="h-8 shrink-0"
          />
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = !item.disabled && activeTab === item.id;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavClick(item)}
                    data-testid={`nav-${item.id}`}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 transition-all text-left rounded-lg
                      ${isActive 
                        ? 'bg-[#1A2B42] text-[#00D9C8] border-l-2 border-[#00D9C8]' 
                        : item.disabled
                          ? 'text-[#5A6C7D] hover:bg-[#1A2B42]/30 cursor-not-allowed'
                          : 'text-[#A0AEC0] hover:text-white hover:bg-[#1A2B42]/50'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    <div>
                      <p className="font-medium">{item.label}</p>
                      <p className="text-xs opacity-70">{item.description}</p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t border-[#1A2B42] space-y-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-start gap-3 px-3 h-auto py-3 hover:bg-[#1A2B42]"
              >
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-[#00D9C8]/20 text-[#00D9C8] text-sm">
                    XC
                  </AvatarFallback>
                </Avatar>
                <div className="text-left">
                  <p className="text-sm font-medium text-white">Demo User</p>
                  <p className="text-xs text-[#5A6C7D] truncate max-w-[140px]">
                    demo@xchannels.ai
                  </p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 bg-[#1A2B42] border-[#0A1628]">
              <DropdownMenuItem className="text-white hover:bg-[#0A1628]">
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#0A1628]" />
              <DropdownMenuItem className="text-red-400 focus:text-red-400 hover:bg-[#0A1628]">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="pt-4 border-t border-[#1A2B42] flex gap-3 text-xs text-[#5A6C7D]">
            <button className="hover:text-[#A0AEC0]">Impressum</button>
            <button className="hover:text-[#A0AEC0]">Datenschutz</button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto bg-[#0A1628]">
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default ShowcaseDashboardLayout;
